/**
 * The one binding between `@heidi/commerce`'s `screen_name` tokens and the app
 * `ScreenName` enum members that declare them.
 *
 * WHY ONE MAP AND NOT ONE PER HOST. Six emit hosts in this directory emit for
 * the trials-and-upgrades surfaces, and `screen_name` crosses between them: the
 * team upgrade-request flow is opened from the recap modal and the trial-ended
 * modal, so more than one host reaches the same token. Six partial maps would be
 * six chances for one row to acquire a second spelling, which is the failure the
 * token/enum binding exists to prevent. `satisfies Record<…>` makes the map
 * TOTAL over the token union, so a token added to the package without an enum
 * member fails here rather than at the first emit.
 *
 * WHY THE BINDING IS NEEDED AT ALL. `modal_viewed`, `modal_dismissed` and
 * `button_clicked` are the app's cross-domain generic events, so their names are
 * not the commerce domain's to declare (D9 ruling 27) and the `ScreenName`
 * vocabulary stays app-side. This map is the one place a package token meets it —
 * which is what keeps the strong `TrackingEventV2` union check running over a
 * payload the domain built.
 *
 * THE NUDGE IS NOT HERE. `packages/commerce/src/contracts/trial-upgrade-nudge-analytics.types.ts`
 * declares its own two screen tokens and `trackTrialUpgradeNudgeAnalytics.ts`
 * binds them; folding them in would mean re-minting tokens that are already
 * declared once, which is the thing this file is against.
 */
import { ScreenName } from '@heidi/analytics/vocabulary';
import {
  TRIALS_AND_UPGRADES_SCREEN,
  type TrialsAndUpgradesScreenToken,
} from '@heidi/commerce/contracts';

export const TRIALS_AND_UPGRADES_SCREEN_NAME_BY_TOKEN = {
  [TRIALS_AND_UPGRADES_SCREEN.clinicianTrialEnded]: ScreenName.ClinicianTrialEndedModal,
  [TRIALS_AND_UPGRADES_SCREEN.practiceTrialEnded]: ScreenName.PracticeTrialEndedModal,
  [TRIALS_AND_UPGRADES_SCREEN.evidencePostUpgradeOnboarding]:
    ScreenName.EvidencePostUpgradeOnboardingModal,
  [TRIALS_AND_UPGRADES_SCREEN.evidencePostUpgradeWelcome]:
    ScreenName.EvidencePostUpgradeWelcomePopup,
  [TRIALS_AND_UPGRADES_SCREEN.ninetyDayTrialEndDiscreet]:
    ScreenName.NinetyDayTrialEndDiscreetPopup,
  [TRIALS_AND_UPGRADES_SCREEN.ninetyDayTrialEndingSoonReminder]:
    ScreenName.NinetyDayTrialEndingSoonReminderPopup,
  [TRIALS_AND_UPGRADES_SCREEN.ninetyDayTrialRecap]: ScreenName.NinetyDayTrialRecapModal,
  [TRIALS_AND_UPGRADES_SCREEN.practiceInviteTeam]: ScreenName.PracticeInviteTeamModal,
  [TRIALS_AND_UPGRADES_SCREEN.practiceTrial]: ScreenName.PracticeTrialModal,
  [TRIALS_AND_UPGRADES_SCREEN.teamTrialUpgradeRequest]:
    ScreenName.TeamTrialUpgradeRequestModal,
  [TRIALS_AND_UPGRADES_SCREEN.trialUpgradeRequestCallback]:
    ScreenName.TrialUpgradeRequestCallbackModal,
} as const satisfies Record<TrialsAndUpgradesScreenToken, ScreenName>;
